import { getCookieDomain } from '@crossnokaye/ux-smarts';

import { COOKIE_DOMAIN, SERVER_URL, StorageKey } from './constants';
import { cookieStorage } from './cookies';

export type AtlasEnv = 'janeway' | 'sandbox' | 'office' | 'live';
const isLocal = window.location.origin.includes('https://login.atlaslocal.io:8444');
const isTesting = window.location.origin.includes('https://localhost:63315');

export type EnvironmentConfig = {
  atlasEnv: AtlasEnv;
  atlasEnvUrl: URL;
  authDomain: string;
  authAudience: string;
  authClientId: string;
  refreshClientId: string;
  launchDarklyClientSideId: string;
};

// TODO: Move to BFF if/when we have a BFF for the Login app
const EnvironmentConfigs: Record<AtlasEnv, EnvironmentConfig> = {
  janeway: {
    atlasEnv: 'janeway',
    atlasEnvUrl: new URL('https://atlasjaneway.io'),
    authAudience: 'https://atlasjaneway.io',
    authDomain: 'auth.atlasjaneway.io',
    authClientId: '9CGZ7nEFCEdyyQBejk4xq00xu8TfidmX',
    refreshClientId: 'kuuE7bjiS7DUPf7FZtTnYRIXoE1ILxPn',
    launchDarklyClientSideId: '68863f6c99213909b6b6257d',
  },
  sandbox: {
    atlasEnv: 'sandbox',
    atlasEnvUrl: new URL('https://atlassandbox.io'),
    authAudience: 'https://atlassandbox.io',
    authDomain: 'auth.atlassandbox.io',
    authClientId: 'ZKakZYisUmf6Ys2G7m4t7JK9T4U8J7pw',
    refreshClientId: 'RfmjBh1MSQfLqg8pHa3dsSn59TeqYIUE',
    launchDarklyClientSideId: '68863f6c99213909b6b6257d',
  },
  office: {
    atlasEnv: 'office',
    atlasEnvUrl: new URL('https://atlasoffice.io'),
    authAudience: 'https://atlasoffice.io',
    authDomain: 'auth.atlasoffice.io',
    authClientId: '7LiRrAOdL3ZlP2crq7VRPRRgHGP8ALAW',
    refreshClientId: 'SLJvrxWh63mvYuJdyyB0vu6cBQmQFHnL',
    launchDarklyClientSideId: '68863f6c99213909b6b6257d',
  },
  live: {
    atlasEnv: 'live',
    atlasEnvUrl: new URL('https://atlaslive.io'),
    authAudience: 'https://atlaslive.io',
    authDomain: 'auth.atlaslive.io',
    authClientId: 'Px5i1QPCfeUK75cB0noqU2YwDDvV3l0t',
    refreshClientId: 'GfyKDNzKPYqO36iQ8ZsUkdylMWqpLoo3',
    launchDarklyClientSideId: '67bdde1dd8a64909e260f4e2',
  },
};

const getAtlasEnv = () => {
  if (isLocal || isTesting) {
    return import.meta.env.VITE_ATLAS_ENV ?? 'sandbox';
  }

  // pull the env out of any url like login.atlas{theenv}.io/admin/tokens.  with any number of subdomains
  return SERVER_URL.hostname.split('.')[0].replace('atlas', '');
};

export const clearBranch = () => {
  document.cookie = `${StorageKey.loaderBranch}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
  document.cookie = `${StorageKey.loaderBranch}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=.login${COOKIE_DOMAIN}`;

  window.location.reload();
};

const atlasEnv: AtlasEnv = getAtlasEnv();
const envConfigs: EnvironmentConfig = EnvironmentConfigs[atlasEnv];
const atlasEnvUrl = envConfigs.atlasEnvUrl;
const loaderBranch = cookieStorage.getItem(StorageKey.loaderBranch) ?? 'main';

// TODO: Move to BFF if/when we have a BFF for the Login app
export const AppConfigs = {
  apiUrl: isLocal ? new URL('https://login.atlaslocal.io:8444') : envConfigs.atlasEnvUrl,
  uiBranch: isLocal ? 'local' : loaderBranch,
  atlasEnv: atlasEnv,
  atlasEnvUrl: atlasEnvUrl,
  isLocal: isLocal,
  isDevelopment: import.meta.env.DEV,
  isProduction: atlasEnv === 'live',
  launchDarklyClientSideId: envConfigs.launchDarklyClientSideId,
};

// TODO: Move to BFF if/when we have a BFF for the Login app
export const AuthConfigs = {
  domain: envConfigs.authDomain,
  clientId: envConfigs.authClientId,
  cacheLocation: 'localstorage',
  cookieDomain: getCookieDomain(window.location.hostname),
  authorizationParams: {
    redirect_uri: window.location.origin + '/admin',
    audience: envConfigs.authAudience,
    scope: 'openid profile email write',
  },
} as const;

// Configuration for user-managed API refresh tokens (not Auth0 authentication tokens)
export const UserRefreshTokenConfig = {
  domain: envConfigs.authDomain,
  clientId: envConfigs.refreshClientId,
  authorizationParams: {
    audience: envConfigs.authAudience,
    scope: 'openid offline_access write',
  },
} as const;
